import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { Form } from 'antd';
import BaseModal from 'basic-ui-lib/components/Modal/BaseModal';
import styled from 'styled-components';
import InputLarge from 'basic-ui-lib/components/Input/InputLarge';
import storeApis from 'modules/store/services/apis';
import toast from 'utils/toast';
import { getMessageError } from 'services/response';
import { setPageLoading } from 'widgets/PageLoading';
import { useMediaQuery } from 'react-responsive';
import BaseInput from 'basic-ui-lib/components/Input/BaseInput';
import { Colors } from 'basic-ui-lib/constants';

export type SyncStoreModalRef = {
  open: () => void;
  close: () => void;
};

type Props = {
  onSuccess?: () => void;
};

interface FormValues {
  email: string;
}

const SyncStoreModal = forwardRef<SyncStoreModalRef, Props>(({ onSuccess }, ref) => {
  const [form] = Form.useForm<FormValues>();
  const [isOpen, setIsOpen] = useState(false);
  const isMobile = useMediaQuery({ maxWidth: 768 });

  useImperativeHandle(ref, () => ({
    open: () => { form.resetFields(); setIsOpen(true); },
    close: () => setIsOpen(false),
  }));

  const onClose = () => {
    setIsOpen(false);
    form.resetFields();
  };

  const handleSubmit = () => {
    form.submit();
  };

  const handleFinish = async (values: FormValues) => {
    try {
      setPageLoading(true);
      const res = await storeApis.syncStore({ email: values.email.trim() });
      if (res?.data?.data) {
        toast.success('Store synced successfully');
        onSuccess?.();
        onClose();
      }
    } catch (error) {
      toast.error(getMessageError(error) || 'Failed to sync store');
    } finally {
      setPageLoading(false);
    }
  };

  if (isMobile) {
    return (
      <BaseModal
        title='Sync Store'
        isOpen={isOpen}
        width='100%'
        onClose={onClose}
        okText='OK'
        onOk={handleSubmit}
        isMobile={true}
      >
        <Container>
          <Form layout='vertical' form={form} onFinish={handleFinish}>
            <Form.Item
                name='email'
                rules={[
                  { required: true, message: 'Email is required' },
                  { type: 'email', message: 'Invalid email format' },
                ]}
              >
              <BaseInput placeholder='Store Email' autoFocus/>
            </Form.Item>
          </Form>
        </Container>
      </BaseModal>
    );
  }

  return (
    <BaseModal
      title='Sync Store'
      isOpen={isOpen}
      width={1000}
      onClose={onClose}
      okText='OK'
      onOk={handleSubmit}
    >
      <Container>
        <Form layout='vertical' form={form} onFinish={handleFinish}>
          <Form.Item
              name='email'
              rules={[
                { required: true, message: 'Email is required' },
                { type: 'email', message: 'Invalid email format' },
              ]}
            >
            <InputLarge placeholder='Store Email' autoFocus />
          </Form.Item>
        </Form>
      </Container>
    </BaseModal>
  );
});

SyncStoreModal.displayName = 'SyncStoreModal';
export default SyncStoreModal;

export const useSyncStoreModalRef = () => useRef<SyncStoreModalRef>(null);

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  padding: 0 50px 60px 50px;

  @media (max-width: 768px) {
    padding: 16px 16px 32px;
    background: ${Colors.neutral.bg[2]};
  }
    
  .ant-form-item {
    margin-bottom: 0;
  }
`;

